// app-pages/Creations.tsx
"use client";
import { Avatar, Image, Text, Box, Flex } from "@chakra-ui/react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useAppContext } from "hooks";
import { useGetAllCreations } from "queries";
import { SkeletonCard, ModelsCreationsLayout } from "components";
import { Model } from "types";

export const Creations = () => {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const { data, fetchNextPage, hasNextPage } = useGetAllCreations(16, selectedTags);

  const toggleTag = (tag: string) => {
    setSelectedTags((prevTags) =>
      prevTags.includes(tag) ? prevTags.filter((t) => t !== tag) : [...prevTags, tag]
    );
  };

  return (
    <Flex direction="column">
      <ModelsCreationsLayout
        JumbotronTitle="My Creations"
        data={
          data
            ? data?.pages.flatMap((group: any) => group.models)
            : Array(16)
                .fill(0)
                .map((_, index) => <SkeletonCard key={index} />)
        }
        renderCard={(model: Model) =>
          data ? <CreationCard key={model.id} model={model} /> : <SkeletonCard />
        }
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        selectedTags={selectedTags}
        toggleTag={toggleTag}
      />
    </Flex>
  );
};

const CreationCard = ({ model }: { model: Model }) => {
  const router = useRouter();
  const { setModelId } = useAppContext();

  const handleNavigation = (model: Model) => {
    setModelId(model.id);
    router.push(`/creations/${model.id}`);
  };

  return (
    <Flex _hover={{ cursor: "pointer" }} onClick={() => handleNavigation(model)} direction="column">
      <Flex position="relative">
        <Box
          bg="black"
          borderRadius="32px"
          position="absolute"
          top={{ base: "2", lg: "4" }}
          right={{ base: "2", lg: "4" }}
          zIndex="1"
          p={{ base: "4px", lg: "8px" }}
        >
          <Text fontWeight="semibold" fontSize="sm" color="white">
            {model.total_image_generations} Creations
          </Text>
        </Box>
        <Image
          boxShadow="0 0 5px 5px rgb(44, 49, 77)"
          borderRadius="14px"
          h={{ base: "142px", md: "180px", lg: "264px" }}
          w={{ base: "142px", md: "180px", lg: "264px" }}
          src={model.thumbnail}
          objectFit="cover"
        />
        <Box
          position="absolute"
          translateY={90}
          bottom={{ base: "1px", md: "5px" }}
          left="2"
          zIndex="1"
          p="8px"
        >
          <Flex align="center">
            <Avatar w="30px" h="30px" src={model.picture} />
            <Flex ml={{ base: "0px", lg: "8px" }} direction="column">
              <Text
                fontWeight="semibold"
                fontSize={{ base: "14px", lg: "16px" }}
                color="white"
                textShadow="0px 0px 8px rgba(0, 0, 0, 0.8)" // Adjust the shadow as needed
              >
                {model.display_name}
              </Text>
              {model.social_media_accounts !== null && model.social_media_accounts.length > 0 && (
                <Text
                  fontSize="12px"
                  color="white"
                  textShadow="0px 0px 8px rgba(0, 0, 0, 0.8)"
                  isTruncated
                  maxW="260px"
                  w="75px"
                >
                  @{model.social_media_accounts[0]?.name}
                </Text>
              )}
            </Flex>
          </Flex>
        </Box>
      </Flex>
      <Flex mt="16px" justify="start">
        {model?.tags?.map((tag: { name: string }, index: number) => (
          <Flex
            px="15px"
            py="3px"
            borderRadius="32px"
            bg="#252A41"
            color="white"
            border="2px solid #2C314D"
            fontSize="12px"
            ml={index && "5px"}
          >
            {tag?.name}
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};

export default Creations;
